import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { CheckCircle2, BookOpen } from "lucide-react";
export default function ExploreOurCourseCatalog() {
  const courses = [{
    id: 1,
    title: "Fundamentos del Stock Market",
    description: "Aprende los conceptos básicos del mercado de valores estadounidense, desde acciones hasta índices bursátiles.",
    outcomes: ["Entender qué son las acciones y cómo se negocian", "Conocer los principales índices: S&P 500, Dow Jones, Nasdaq", "Identificar factores que mueven los precios"],
    prerequisites: "Ninguno - ideal para principiantes",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/fundamentals_stock_market_course.png"
  }, {
    id: 2,
    title: "Análisis Técnico Avanzado",
    description: "Domina gráficos de velas, patrones de precio, y utiliza indicadores técnicos para identificar oportunidades de inversión.",
    outcomes: ["Leer gráficos de velas japonesas", "Aplicar indicadores técnicos (RSI, MACD, Medias Móviles)", "Detectar patrones de continuación y reversión"],
    prerequisites: "Conocimientos básicos de mercado de valores",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/technical_analysis_advanced_course.png"
  }, {
    id: 3,
    title: "Análisis Fundamental",
    description: "Evalúa el valor intrínseco de las empresas mediante estados financieros, ratios y modelos de valoración.",
    outcomes: ["Analizar estados financieros: ingresos, márgenes, flujo de caja", "Calcular ratios clave: P/E, ROE, Deuda/Capital", "Determinar el valor justo de una acción"],
    prerequisites: "Fundamentos de contabilidad recomendados",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/fundamental_analysis_course.png"
  }, {
    id: 4,
    title: "Estrategias de Trading Intradía",
    description: "Aprende a ejecutar operaciones de corto plazo con disciplina y gestión de riesgo efectiva.",
    outcomes: ["Identificar setups de trading intradía", "Gestionar el riesgo con stop-loss y take-profit", "Desarrollar un plan de trading estructurado"],
    prerequisites: "Experiencia previa en análisis técnico",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/intraday_trading_strategies_course.png"
  }, {
    id: 5,
    title: "Inversión a Largo Plazo",
    description: "Construye un portafolio diversificado orientado al crecimiento sostenido y la generación de riqueza a largo plazo.",
    outcomes: ["Diversificar activos para reducir riesgo", "Seleccionar acciones de crecimiento y dividendos", "Rebalancear el portafolio periódicamente"],
    prerequisites: "Conocimientos básicos de análisis fundamental",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/long_term_investment_course.png"
  }, {
    id: 6,
    title: "Psicología del Trading",
    description: "Controla emociones, evita sesgos cognitivos y mantén disciplina para maximizar tu rendimiento en el mercado.",
    outcomes: ["Reconocer emociones que afectan decisiones", "Aplicar técnicas para mantener la calma bajo presión", "Desarrollar hábitos de disciplina y consistencia"],
    prerequisites: "Ninguno - complementa cualquier nivel",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/trading_psychology_course.png"
  }];
  return <section className="bg-background py-16 lg:py-24">
      <div className="container mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-12 text-center animate-fade-in-up">
          <h2 className="text-4xl lg:text-5xl font-semibold text-foreground mb-4">
            Explora Nuestro Catálogo de Cursos
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Desarrolla tus habilidades en inversión con cursos diseñados para
            todos los niveles, desde principiantes hasta traders avanzados.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.map((course, i) => <Card key={course.id} data-index={i} className="card hover-lift border border-[var(--color-border)] shadow-soft">
              <img src={course.image} alt={course.title} className="w-full h-48 object-cover rounded-t-xl" />
              <CardHeader>
                <CardTitle className="text-2xl text-foreground">
                  {course.title}
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  {course.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    Qué Aprenderás
                  </h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {course.outcomes.map((outcome, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2">
                        <span className="text-secondary mt-0.5">•</span>
                        <span>{outcome}</span>
                      </li>)}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-accent" />
                    Prerrequisitos
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {course.prerequisites}
                  </p>
                </div>
              </CardContent>
              <CardFooter>
                <Button asChild className="w-full btn-primary">
                  <Link to="/CursosPage">Ver Detalles</Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        <div className="mt-12 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious href="#" />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive>
                  1
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#">2</PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#">3</PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
              <PaginationItem>
                <PaginationNext href="#" />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </section>;
}